<script setup>
import { useAlert } from 'dashboard/composables';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import SectionLayout from '../../../../components-next/SectionLayout.vue';
import LucideIcon from '../../../../components/LucideIcon.vue';
const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});

const inboxesList = computed(() => getters['inboxes/getInboxes'].value);
const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'), {type: 'success'});
    
    // Trực tiếp refresh conversation stats sau khi xóa inbox thành công
    store.dispatch('conversationStats/get');
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'), {type: 'error'});
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};

const isZaloPersonal = inbox => inbox.channel_type === 'Channel::ZaloPersonal';
const isFacebook = inbox => (inbox.channel_type || '').includes('Facebook');

const getConnectionStatus = inbox => {
  if (isZaloPersonal(inbox) || isFacebook(inbox)) {
    const needsReauth = !!inbox.reauthorization_required;
    return needsReauth
      ? { label: t('INBOX_MGMT.CONNECTION_STATUS.DANGER'), variant: 'danger' }
      : { label: t('INBOX_MGMT.CONNECTION_STATUS.SUCCESS'), variant: 'success' };
  }
  return null;
};

const statusBadgeClass = variant => {
  switch (variant) {
    case 'success':
      return 'bg-grass-100 text-grass-700 dark:bg-grass-600 dark:text-grass-100 border-grass-600 dark:border-grass-800';
    case 'danger':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-800';
    default:
      return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-100 border-slate-200 dark:border-slate-600';
  }
};

// Ensure fresh data on settings page: fetch inboxes without cache
onMounted(() => {
  store.dispatch('inboxes/getWithoutCache');
});
</script>

<template>
  <SectionLayout :title="$t('INBOX_MGMT.HEADER')">
    <template #header-left>
      <router-link
        v-if="isAdmin"
        class="rounded-lg input-base !bg-grass-500 flex items-center gap-2 text-white text-sm font-semibold"
          :to="{ name: 'settings_inbox_new' }"
      >
      {{ $t('SETTINGS.INBOXES.NEW_INBOX') }}
      <LucideIcon name="plus"/>
      </router-link>
    </template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        feature-name="inboxes"
      >    
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="setting-table-container">
        <table>
          <tbody
            class="divide-y divide-slate-25 dark:divide-slate-800 flex-1 text-slate-700 dark:text-slate-100"
          >
            <tr v-for="inbox in inboxesList" :key="inbox.id">
              <td class="py-2 ltr:pr-4 rtl:pl-4">
                <div class="flex items-center flex-row gap-4">
                  <Thumbnail
                    v-if="inbox.avatar_url"
                    class="bg-black-50 dark:bg-black-800 rounded-full"
                    :src="inbox.avatar_url"
                    :username="inbox.name"
                    size="32px"
                  />
                  <div
                    v-else
                    class="w-8 h-8 bg-black-50 dark:bg-black-800 rounded-full block"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      class="opacity-80 p-1"
                    >
                      <path
                        fill="currentColor"
                        d="M1 12c0-5.185 0-7.778 1.61-9.39C4.223 1 6.816 1 12 1s7.778 0 9.39 1.61C23 4.223 23 6.816 23 12s0 7.778-1.61 9.39C19.777 23 17.184 23 12 23s-7.778 0-9.39-1.61C1 19.777 1 17.184 1 12"
                        opacity=".35"
                      />
                      <path
                        fill="currentColor"
                        d="M2.61 21.389c1.612 1.61 4.205 1.61 9.39 1.61s7.778 0 9.39-1.61c1.492-1.493 1.601-3.829 1.61-8.29h-3.476c-.996 0-1.494 0-1.931.202c-.438.201-.762.58-1.41 1.335l-.666.777c-.648.756-.972 1.134-1.41 1.335s-.935.202-1.93.202h-.353c-.996 0-1.494 0-1.931-.202c-.438-.2-.762-.579-1.41-1.335l-.666-.777c-.648-.756-.972-1.134-1.41-1.335s-.935-.201-1.93-.201H1c.008 4.46.118 6.796 1.61 8.289"
                      />
                    </svg>
                  </div>
                  <div>
                    <span class="block font-medium capitalize">
                      {{ inbox.name }}
                    </span>
                    <ChannelName
                      :channel-type="inbox.channel_type"
                      :medium="inbox.medium"
                    />
                    <div v-if="getConnectionStatus(inbox)" class="mt-1">
                      <span
                        class="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium"
                        :class="statusBadgeClass(getConnectionStatus(inbox).variant)"
                        v-tooltip.top="getConnectionStatus(inbox).label"
                      >
                        {{ getConnectionStatus(inbox).label }}
                      </span>
                    </div>
                  </div>
                </div>
              </td>

              <td class="py-2">
                <div class="flex gap-1 justify-end">
                  <router-link
                    :to="{
                      name: 'settings_inbox_show',
                      params: { inboxId: inbox.id },
                    }"
                  >
                    <woot-button
                      v-if="isAdmin"
                      v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                      variant="smooth"
                      size="tiny"
                      icon="edit"
                      color-scheme="secondary"
                      class-names="!rounded-lg"
                    />
                  </router-link>

                  <woot-button
                    v-if="isAdmin"
                    v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
                    variant="smooth"
                    color-scheme="secondary"
                    size="tiny"
                    class-names="!rounded-lg"
                    icon="dismiss-circle"
                    @click="openDelete(inbox)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
  </SectionLayout>
</template>
